import React, { useState, useMemo } from 'react';
import {
    Popover,
    Typography,
    Alert,
    useTheme
} from '@mui/material';
import ReservationForm, { ReservationFormData } from './ReservationForm';

interface Board {
    id: string;
    name: string;
    type: string;
}

interface Reservation {
    id: string;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    startDate: string;
    endDate: string;
    status: 'pending' | 'confirmed' | 'cancelled';
    paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
    board: {
        id: string;
        name: string;
        type: string;
    };
    notes?: string;
}

interface EditReservationPopoverProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    onEditSuccess: () => void;
    reservation: Reservation | null;
    boards: Board[];
    existingReservations?: Reservation[];
}

const EditReservationPopover: React.FC<EditReservationPopoverProps> = ({
    anchorEl,
    open,
    onClose,
    onEditSuccess,
    reservation,
    boards,
    existingReservations = []
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const initialData = useMemo(() => {
        if (!reservation) return undefined;
        const start = new Date(reservation.startDate);
        const end = new Date(reservation.endDate);

        return {
            customerName: reservation.customerName,
            customerEmail: reservation.customerEmail || '',
            customerPhone: reservation.customerPhone || '',
            boardId: reservation.board.id,
            startDate: start,
            endDate: end,
            startTime: start,
            endTime: end,
            notes: reservation.notes || '',
            status: reservation.status,
            paymentStatus: reservation.paymentStatus || 'pending'
        };
    }, [reservation]);

    const handleSubmit = async (data: ReservationFormData) => {
        if (!reservation) return;
        setLoading(true);
        setError('');

        try {
            const start = new Date(data.startDate);
            start.setHours(data.startTime.getHours(), data.startTime.getMinutes());

            const end = new Date(data.endDate);
            end.setHours(data.endTime.getHours(), data.endTime.getMinutes());

            const API_URL = process.env.REACT_APP_API_URL || 'https://api.turn.ar';
            const response = await fetch(`${API_URL}/api/reservations/${reservation.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    customerName: data.customerName,
                    customerEmail: data.customerEmail,
                    customerPhone: data.customerPhone,
                    startDate: start.toISOString(),
                    endDate: end.toISOString(),
                    notes: data.notes,
                    status: data.status,
                    paymentStatus: data.paymentStatus,
                    boardId: data.boardId,
                    rescheduleDate: data.rescheduleDate ? data.rescheduleDate.toISOString() : undefined
                })
            });

            if (!response.ok) {
                const resData = await response.json();
                throw new Error(resData.message || 'Error al actualizar la reserva');
            }

            onEditSuccess();
            onClose();

        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    // Filter out the current reservation from existingReservations to avoid self-conflict
    const otherReservations = useMemo(() => {
        return existingReservations?.filter(r => r.id !== reservation?.id) || [];
    }, [existingReservations, reservation]);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                sx: { p: 3, width: 400, borderRadius: 2, bgcolor: isDark ? 'grey.800' : 'background.paper' }
            }}
        >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Editar Reserva
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {reservation && (
                <ReservationForm
                    initialData={initialData}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                    boards={boards}
                    existingReservations={otherReservations as any}
                    isEditMode={true}
                    loading={loading}
                />
            )}
        </Popover>
    );
};

export default EditReservationPopover;
